import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, User, PlusCircle, FileX, TimerReset,
  LogOut, ChevronDown, HelpCircle, Menu, X, Shield
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import Footer from './Footer';
import VahanfinMeta from './VahanfinMeta';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isImpersonating, impersonatedBy, returnToAdmin } = useAuthStore();
  
  const handleLogout = () => {
    const returningToAdmin = isImpersonating;
    logout();
    toast.success(returningToAdmin ? 'Returned to admin account' : 'Logged out successfully');
    navigate(returningToAdmin ? '/admin' : '/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/profile', label: 'My Profile', icon: <User className="w-5 h-5" /> },
    { 
      label: 'Add Hypothecation', 
      icon: <PlusCircle className="w-5 h-5" />,
      children: [
        { path: '/add-by-engine', label: 'By Engine/Chassis' },
        { path: '/add-by-registration', label: 'By Registration' },
      ]
    },
    { path: '/terminate', label: 'Terminate Hypothecation', icon: <FileX className="w-5 h-5" /> },
    { path: '/continue', label: 'Continue Hypothecation', icon: <TimerReset className="w-5 h-5" /> },
  ];

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <VahanfinMeta />
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:relative lg:translate-x-0 transition duration-300 ease-in-out`}>
        <div className="h-full flex flex-col">
          {/* Mobile close button */}
          <div className="lg:hidden absolute right-0 top-0 p-1">
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Sidebar header */}
            <div className="h-16 flex items-center justify-center border-b px-4">
            <img 
              src="/logo/jk.png" 
              alt="Bank Logo" 
              className="h-10 w-30"
            />
            </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="px-2 space-y-1">
              {navItems.map((item, index) => (
                !item.children ? (
                  <Link
                    key={index}
                    to={item.path}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      location.pathname === item.path
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    <span className="ml-3">{item.label}</span>
                  </Link>
                ) : (
                  <div key={index} className="space-y-1">
                    <button
                      type="button"
                      className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900"
                      onClick={() => {
                        const elem = document.getElementById(`submenu-${index}`);
                        if (elem) {
                          elem.classList.toggle('hidden');
                        }
                      }}
                    >
                      {item.icon}
                      <span className="ml-3">{item.label}</span>
                      <ChevronDown className="ml-auto h-4 w-4" />
                    </button>
                    <div id={`submenu-${index}`} className="pl-8 space-y-1">
                      {item.children.map((child, childIndex) => (
                        <Link
                          key={childIndex}
                          to={child.path}
                          className={`group flex items-center px-3 py-2 text-xs font-medium rounded-md ${
                            location.pathname === child.path
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <span className="truncate">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              ))}
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Link
                  to="/admin"
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    location.pathname === '/admin'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Shield className="w-5 h-5" />
                  <span className="ml-3">Admin Panel</span>
                </Link>
              )}
            </nav>
          </div>

          {/* Sidebar footer */}
          <div className="border-t p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-auto p-1 rounded-full text-gray-500 hover:bg-gray-100"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {isImpersonating && impersonatedBy && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-900">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="truncate">
                Viewing as <strong>{user?.name}</strong>. Admin: <strong>{impersonatedBy.name}</strong>
              </span>
              <button
                type="button"
                onClick={() => {
                  returnToAdmin();
                  toast.success('Returned to admin account');
                  navigate('/admin');
                }}
                className="inline-flex h-8 items-center justify-center rounded border border-blue-300 bg-white px-3 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                Return to admin
              </button>
            </div>
          </div>
        )}

        {/* Mobile header with menu toggle */}
        <div className="lg:hidden p-4 border-b flex items-center bg-white sticky top-0 z-10">
          <button 
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 mr-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-gray-800">
              {user?.bankName || 'Bank Hypothecation'}
            </h1>
          </div>
          <div className="flex items-center">
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Link to="/admin" className="mr-2 p-2 rounded-md text-gray-500 hover:bg-gray-100">
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}

export default Layout;
